import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Crown, Lock } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export function ClaimBox({ initial, onClaimed }) {
  const [siret, setSiret] = useState(initial || "");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/pro/claim", { siret });
      toast.success(`${data.name} est maintenant rattachée à votre compte`);
      setSiret("");
      onClaimed(data.id);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="rounded-2xl bg-slate-900 p-6 text-white sm:p-8" data-testid="claim-form">
      <h2 className="font-display text-xl font-bold sm:text-2xl">Revendiquer une entreprise</h2>
      <p className="mt-1 text-sm text-slate-400">Saisissez le numéro SIRET (14 chiffres) de votre établissement. C'est gratuit.</p>
      <div className="mt-5 flex flex-col gap-3 sm:flex-row">
        <Input value={siret} onChange={(e) => setSiret(e.target.value)} placeholder="14 chiffres, ex. 123 456 789 00012" className="h-12 border-slate-700 bg-slate-800 font-mono-id text-white placeholder:text-slate-500" data-testid="claim-siret-input" />
        <Button type="submit" disabled={busy} className="h-12 rounded-xl bg-white px-6 font-semibold text-slate-900 hover:bg-[#D97706] hover:text-white" data-testid="claim-submit-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Revendiquer"}
        </Button>
      </div>
    </form>
  );
}

export function PlanCard({ b, onChange }) {
  const [busy, setBusy] = useState("");
  const checkout = async (plan) => {
    setBusy(plan);
    try {
      const { data } = await api.post("/payments/checkout", { plan, business_id: b.id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(errMsg(err));
      setBusy("");
    }
  };
  const cancel = async () => {
    setBusy("cancel");
    try {
      await api.post(`/pro/businesses/${b.id}/cancel`);
      toast.success("Votre abonnement s'arrêtera à la fin de la période en cours");
      onChange();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy("");
    }
  };
  const until = b.plan_until && new Date(b.plan_until).toLocaleDateString("fr-FR");
  if (b.plan_active) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-[#1E40AF] bg-[#EFF6FF] p-5" data-testid="plan-status-active">
        <div>
          <p className="flex items-center gap-2 font-display text-lg font-bold text-[#1E40AF]"><Crown className="h-5 w-5" />Formule {b.plan_raw === "pro" ? "Pro" : "Essentiel"}</p>
          <p className="text-sm text-slate-600">{b.plan_cancel_at_period_end ? `Se termine le ${until}` : `Renouvellement vers le ${until}`}</p>
        </div>
        <div className="flex gap-2">
          {b.plan_raw === "essentiel" && <Button onClick={() => checkout("pro")} disabled={!!busy} className="bg-slate-900 hover:bg-[#1E40AF] hover:text-white" data-testid="upgrade-pro-button">Passer en Pro · 9,90 €</Button>}
          {!b.plan_cancel_at_period_end && <Button variant="outline" onClick={cancel} disabled={!!busy} data-testid="cancel-subscription-button">Résilier</Button>}
        </div>
      </div>
    );
  }
  return (
    <div className="grid gap-3 sm:grid-cols-2" data-testid="plan-status-inactive">
      {[["essentiel", "Essentiel", "4,90 €", "Infos, marques, compétences"], ["pro", "Pro", "9,90 €", "+ produits en stock et photos"]].map(([id, n, p, d]) => (
        <button key={id} onClick={() => checkout(id)} disabled={!!busy} className={`rounded-xl border p-5 text-left transition-[border-color,transform] hover:-translate-y-0.5 ${id === "pro" ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white hover:border-[#1E40AF]"}`} data-testid={`subscribe-${id}-button`}>
          <p className="font-display text-lg font-bold">{n} <span className="font-extrabold">{p}</span><span className="text-sm font-normal opacity-70">/mois</span></p>
          <p className="mt-1 text-sm opacity-70">{busy === id ? "Redirection vers le paiement…" : d}</p>
        </button>
      ))}
    </div>
  );
}

export function Locked({ need }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-dashed border-slate-300 bg-white p-6 text-sm text-slate-600" data-testid={`locked-${need}`}>
      <Lock className="h-5 w-5 text-slate-400" />Cette section nécessite la formule {need === "pro" ? "Pro (9,90 €/mois)" : "Essentiel (4,90 €/mois) ou Pro"}.
    </div>
  );
}
